import React, { useMemo } from 'react';
import { EEthereumNetworkId } from '@ankr.com/provider';
import { ExternalLink } from '@ankr.com/ui';
import { Box, Typography } from '@mui/material';
import { format } from 'date-fns';

import {
  cropString,
  Status,
  StatusType,
  useChainData,
  useNetworkConfig,
} from 'modules/common';
import { globalTranslation, useTranslation } from 'modules/i18n';

import { GetTaskExecutionsResult } from '../actions/getExecutions';

export interface UseExecutionsContentItem {
  id: string;
  executedAt: React.ReactNode;
  txHash: React.ReactNode;
  status: React.ReactNode;
  statusDot: React.ReactNode;
  cost: React.ReactNode;
}

export function useExecutionsContent(
  data: GetTaskExecutionsResult,
  chainId: EEthereumNetworkId,
): UseExecutionsContentItem[] {
  const config = useNetworkConfig(chainId);
  const chainData = useChainData(chainId);
  const { t, keys } = useTranslation(globalTranslation);

  return useMemo(
    () =>
      data.map(({ executedAt, txHash, cost, usdCost, isSuccess }) => ({
        id: txHash,
        executedAt: (
          <Typography variant="subtitle3">
            {format(executedAt, 'LLL L, yyyy, kk:mm')}
          </Typography>
        ),
        txHash: (
          <Typography variant="subtitle3" display="flex" alignItems="center">
            {cropString(txHash)}

            {config?.urls.explorer && (
              <Box
                component="a"
                href={`${config.urls.explorer}/tx/${txHash}`}
                target="_blank"
                rel="noreferrer"
                display="flex"
                ml={2}
              >
                <ExternalLink
                  sx={theme => ({ color: theme.palette.grey[600] })}
                />
              </Box>
            )}
          </Typography>
        ),
        cost: (
          <Box display="flex" flexDirection="column" justifyContent="flex-end">
            <Typography variant="subtitle3">
              {t(keys.units.tokenAmount, {
                amount: cost.decimalPlaces(6).toString(),
                token: chainData?.tokenName,
              })}
            </Typography>

            {usdCost && !usdCost.isZero() && (
              <Typography
                variant="subtitle3"
                mt={1}
                color={theme => theme.palette.grey[500]}
                textAlign="right"
              >
                (
                {t(keys.units.$, {
                  value: usdCost.decimalPlaces(6).toString(),
                })}
                )
              </Typography>
            )}
          </Box>
        ),
        status: <Status isError={!isSuccess} type={StatusType.Transaction} />,
        statusDot: (
          <Box
            sx={theme => ({
              width: 6,
              height: 6,
              borderRadius: '50%',
              backgroundColor: isSuccess
                ? theme.palette.success.main
                : theme.palette.error.main,
            })}
          />
        ),
      })),
    [chainData, config, data, keys, t],
  );
}
